/* global bankReconDaysBetween, num, t */
// ============================================================
// BANKAVSTEMMING — dekning og samlet saldoavstemming
// ============================================================
// Skilt fra `backend-bank-recon-view.jsx`, som eier innsamlingen av
// bankkontoer, linjer og hovedbokslinjer. Her ligger de to utledningene som
// svarer paa hvor godt grunnlaget dekker perioden, og om banken gaar opp naar
// alle kontoene ses under ett.

// Hvilke dager i måneden bankgrunnlaget faktisk dekker. En kontoutskrift som
// stopper tre dager før månedsskiftet er ikke et komplett grunnlag, og det er
// ikke synlig av saldoen alene.
//
// Dekningen er et SPENN, ikke en telling av dager med bevegelse: en dag uten
// transaksjoner er ikke et hull i grunnlaget. Derfor maales det fra forste til
// siste linje, klippet mot perioden.
const bankReconCoveragePct = (from, to, range) => {
  if (!from || !to || !range.days) return 0;
  const start = from > range.from ? from : range.from;
  const end = to < range.to ? to : range.to;
  if (end < start) return 0;
  const days = bankReconDaysBetween(start, end) + 1;
  return Math.max(0, Math.min(100, Math.round((days / range.days) * 100)));
};

const bankReconCoverage = (rows, range) => {
  if (!rows.length) return { from: null, to: null, pct: 0, complete: false, missingLabel: t('rcb.cov.none', 'Ingen bankutskrift for perioden') };
  const from = rows[0].date;
  const to = rows[rows.length - 1].date;
  const complete = from <= range.from && to >= range.to;
  const pct = bankReconCoveragePct(from, to, range);
  if (complete) return { from, to, pct: 100, complete: true, missingLabel: '' };
  const parts = [];
  const gap = (gapFrom, gapTo) => t('rcb.cov.missingRange', 'mangler fra {from} til {to}', { from: gapFrom, to: gapTo });
  if (from > range.from) parts.push(gap(range.from, from));
  if (to < range.to) parts.push(gap(to, range.to));
  const missingLabel = parts.length === 2
    ? t('rcb.cov.and', '{first} og {second}', { first: parts[0], second: parts[1] })
    : parts.join(' og ');
  return { from, to, pct, complete: false, missingLabel };
};

// Varslene. Hvert av dem peker på noe dataene sier, ikke på en gjetning.

// Den samlede saldoavstemmingen. Bare kontoer der BEGGE sidene finnes teller
// med: en konto uten bankutskrift har ingen differanse aa vaere med i, og aa
// legge den bokforte saldoen inn alene ville laget et avvik som ikke finnes.
// Valutakontoer holdes utenfor av samme grunn som per konto — kroner og valuta
// kan ikke summeres.
const bankReconSummary = ({ accounts, lines, counts, transit }) => {
  const round = (value) => Math.round(value * 100) / 100;
  const comparable = accounts.filter((account) => !account.isForeignCurrency
    && Number.isFinite(account.bookedBalance) && Number.isFinite(account.bankBalance));
  const sum = (key) => round(comparable.reduce((total, account) => total + (account[key] || 0), 0));
  const bookedTotal = sum('bookedBalance');
  const bankTotal = sum('bankBalance');
  const preliminaryDifference = round(bookedTotal - bankTotal);
  // Bokfort paa bankkontoen uten en banklinje er nettopp det som forklarer at
  // hovedboken ligger foran banken. Om hver enkelt av dem er DOKUMENTERT er en
  // faglig vurdering — her sier vi bare hvor mye av differansen de utgjor.
  const transitTotal = round(transit.reduce((total, entry) => total + num(entry.amount), 0));
  return {
    comparableAccounts: comparable.length,
    excludedAccounts: accounts.length - comparable.length,
    bookedTotal,
    bankTotal,
    preliminaryDifference,
    transitTotal,
    unexplainedDifference: round(preliminaryDifference - transitTotal),
    openingTotal: sum('openingBalance'),
    change: round(sum('bookedBalance') - sum('openingBalance')),
    bankLineCount: lines.length,
    bookedLineCount: counts.booked,
    unbookedLineCount: counts.total - counts.booked,
  };
};

Object.assign(window, { bankReconCoverage, bankReconCoveragePct, bankReconSummary });
